import { action, makeObservable, observable, reaction } from 'mobx';
import * as Sentry from '@sentry/browser';

import epaxios from '@/utils/epaxios';
import LogErrorMessage from '@/utils/LogErrorMessage';
import { permissions } from '@/constants';

import type RootStore from '../RootStore';
import type { StationReadinessScoreData } from '@/comp/StationReadinessScore/types';

export default class StationReadinessScoreStore {
  rootStore: typeof RootStore;

  @observable stationReadinessData: StationReadinessScoreData | null = null;
  @observable isLoading = false;
  @observable error: string | null = null;

  constructor(rootStore: typeof RootStore) {
    makeObservable(this);
    this.rootStore = rootStore;

    reaction(
      () => this.rootStore.stationStore.currentStation.devices,
      devices => {
        if (devices && devices.length > 0 && this.stationReadinessData) {
          this.reloadIfPermitted();
        }
      },
    );
  }

  @action getStationScore = async (customerId: string, siteId: string, stationId: string) => {
    this.isLoading = true;
    this.error = null;

    try {
      const { data } = await epaxios.get<StationReadinessScoreData>(
        `info-collection/station-score/customer/${customerId}/site/${siteId}/station/${stationId}`,
      );

      this.stationReadinessData = data;
    } catch (e) {
      if (e.response?.status === 403) {
        this.error = 'Insufficient permissions to view station readiness score';
        LogErrorMessage('Station Readiness Score permission denied', e);
        return;
      }

      this.error = `Failed to fetch station readiness score: ${e}`;
      LogErrorMessage('Station Readiness Score load error', e);
      Sentry.captureException(new Error(this.error));
      throw e;
    } finally {
      this.isLoading = false;
    }
  };

  @action resetData = () => {
    this.stationReadinessData = null;
    this.error = null;
    this.isLoading = false;
  };

  @action clearError = () => {
    this.error = null;
  };

  hasPermissionToView = (): boolean => {
    return this.rootStore.permissionStore.isAuth(permissions.VIEW_STATION_SCORE);
  };

  @action reloadStationScore = async () => {
    const { customerId, siteId, id: stationId } = this.rootStore.stationStore.currentStation;

    if (customerId && siteId && stationId) {
      await this.getStationScore(customerId, siteId, stationId);
    }
  };

  @action reloadIfPermitted = async () => {
    if (!this.hasPermissionToView()) {
      return;
    }
    await this.reloadStationScore();
  };
}
